import { DevicePage } from "./device-page";
import {
  AuthUiApp,
  AuthUiProvider,
  type AuthLocale,
  type ClientBranding,
  type ConsentRequest,
} from "@elefante/auth-ui";
import "@elefante/auth-ui/styles.css";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { createUiApi } from "./api";
import { consumeEmailActionToken } from "./email-action-token";

const DEFAULT_CLIENT: ClientBranding = {
  clientId: "elefante-account",
  name: "Elefante",
  homeUrl: "https://elefante.dev",
  enabledMethods: ["password", "google", "magic-link", "passkey"],
};

void bootstrap();

async function bootstrap(): Promise<void> {
  const container = document.getElementById("root");
  if (!container) throw new Error("Missing root element");
  const { searchParams: params } = consumeEmailActionToken(
    window.location,
    (url) => window.history.replaceState(window.history.state, "", url),
  );
  const clientId = params.get("client_id");
  const client = await loadClient(clientId);
  const consentRequest =
    window.location.pathname === "/consent"
      ? buildConsentRequest(params, client)
      : undefined;
  const locale = localeFromParams(params);

  createRoot(container).render(
    <StrictMode>
      {window.location.pathname === "/device" ? (
        <AuthUiProvider api={createUiApi()} client={client}>
          <DevicePage initialCode={params.get("user_code") ?? ""} />
        </AuthUiProvider>
      ) : (
        <AuthUiApp
          api={createUiApi()}
          client={client}
          path={window.location.pathname}
          searchParams={params}
          {...(consentRequest ? { consentRequest } : {})}
          {...(locale ? { initialLocale: locale } : {})}
          onNavigate={(url) => window.location.assign(url)}
        />
      )}
    </StrictMode>,
  );
}

async function loadClient(clientId: string | null): Promise<ClientBranding> {
  if (!clientId) return DEFAULT_CLIENT;
  const response = await fetch(
    `/api/portal/client?client_id=${encodeURIComponent(clientId)}`,
    {
      credentials: "same-origin",
    },
  );
  if (!response.ok)
    return { ...DEFAULT_CLIENT, clientId, name: "Aplicativo Elefante" };
  return response.json();
}

function buildConsentRequest(
  params: URLSearchParams,
  client: ClientBranding,
): ConsentRequest | undefined {
  const scopes = params.get("scope")?.split(/\s+/).filter(Boolean) ?? [];
  if (!params.get("client_id") || scopes.length === 0) return undefined;
  return {
    id: window.location.search.replace(/^\?/, ""),
    client,
    scopes,
  };
}

function localeFromParams(params: URLSearchParams): AuthLocale | undefined {
  const value = params.get("ui_locales")?.split(/\s+/)[0]?.toLowerCase();
  if (value?.startsWith("en")) return "en";
  if (value?.startsWith("pt")) return "pt-BR";
  return undefined;
}
